import { useEffect, useMemo, useState } from "react";
import { getCompanyOfficers } from "../../data/request";
import { Officer } from "../../types/Officer";
import * as Utilities from "../../data/utilities";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TableContainer,
  TextField,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  Chip,
  TablePagination,
} from "@mui/material";
import { Link } from "react-router-dom";
import { DELTA_ACCENT, STATUS_GREEN, STATUS_GREY } from "../colors";

const ROWS_PER_PAGE = 15;

export const OfficersTable = (props: { companyNo: string | undefined }) => {
  const { companyNo } = props;

  const [officers, setOfficers] = useState<Officer[]>([]);
  const [orderBy, setOrderBy] = useState<keyof Officer>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [filter, setFilter] = useState("");
  const [showResigned, setShowResigned] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const transform = (officer_array: Officer[]): Officer[] => {
    return officer_array.map((item) => {
      const status = item.resigned_on === undefined ? "Active" : "Resigned";
      return {
        ...item,
        status,
        occupation: Utilities.getValueDisplay(item.occupation ?? item.officer_role),
      };
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (companyNo != undefined) {
        setLoading(true);
        const doc = await getCompanyOfficers(companyNo);
        setOfficers(transform(doc.items ?? []));
        setLoading(false);
      }
    };

    fetchData();
  }, [companyNo]);

  const resignedCount = officers.filter((o) => o.status === "Resigned").length;

  const visibleOfficers = useMemo(() => {
    let result = officers;

    if (!showResigned) {
      result = result.filter((o) => o.status === "Active");
    }

    if (filter) {
      const lower = filter.toLowerCase();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(lower) ||
          item.nationality?.toLowerCase().includes(lower)
      );
    }

    const sorted = [...result].sort((a, b) => {
      const aVal = a[orderBy] ?? "";
      const bVal = b[orderBy] ?? "";
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [officers, showResigned, filter, orderBy, order]);

  const handleSort = (property: keyof Officer) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pagedOfficers = visibleOfficers.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box style={{ padding: 10 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          padding: "20px 10px 20px",
        }}
      >
        {officers.length > 10 && (
          <TextField
            label="Filter by name or nationality"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0);
            }}
            fullWidth
            size="small"
          />
        )}
        {resignedCount > 0 && (
          <FormControlLabel
            control={
              <Switch
                checked={showResigned}
                onChange={(e) => {
                  setShowResigned(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label={`Show resigned (${resignedCount})`}
            style={{ whiteSpace: "nowrap" }}
          />
        )}
      </Box>

      <TableContainer style={{ overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === "name"}
                direction={orderBy === "name" ? order : "asc"}
                onClick={() => handleSort("name")}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "occupation"}
                direction={orderBy === "occupation" ? order : "asc"}
                onClick={() => handleSort("occupation")}
              >
                Occupation
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "officer_role"}
                direction={orderBy === "officer_role" ? order : "asc"}
                onClick={() => handleSort("officer_role")}
              >
                Officer Role
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "nationality"}
                direction={orderBy === "nationality" ? order : "asc"}
                onClick={() => handleSort("nationality")}
              >
                Nationality
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "status"}
                direction={orderBy === "status" ? order : "asc"}
                onClick={() => handleSort("status")}
              >
                Status
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedOfficers.map((item) => (
            <TableRow key={item.links?.self ?? item.name}>
              <TableCell>
                <Link
                  to={item.links.officer.appointments}
                  style={{ textDecoration: "none", color: DELTA_ACCENT, fontWeight: 600 }}
                >
                  {Utilities.getValueDisplay(item.name)}
                </Link>
              </TableCell>
              <TableCell>{Utilities.getValueDisplay(item.occupation)}</TableCell>
              <TableCell>{Utilities.getValueDisplay(item.officer_role)}</TableCell>
              <TableCell>{Utilities.getValueDisplay(item.nationality)}</TableCell>
              <TableCell>
                <Chip
                  label={item.status}
                  size="small"
                  style={{
                    backgroundColor: item.status === "Active" ? STATUS_GREEN : STATUS_GREY,
                    color: "white",
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>

      {visibleOfficers.length === 0 && (
        <Box style={{ padding: 20 }}>No officers match this filter.</Box>
      )}

      {visibleOfficers.length > 0 && (
        <TablePagination
          component="div"
          count={visibleOfficers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      )}
    </Box>
  );
};

export default OfficersTable;
